import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { getSupabaseDbError } from '@/lib/errors';
import type { FriendlyError } from '@/lib/errors';
import { captureFriendlyError } from '@/lib/sentry';
import type { Profile, OnboardingData } from '@/types/profile';

export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    logger.error('Error fetching profile', error, { userId });
    return null;
  }

  return data as Profile;
}

export async function updateProfile(
  userId: string,
  data: OnboardingData
): Promise<{ success: boolean; error?: FriendlyError }> {
  const { error } = await supabase
    .from('profiles')
    .update({
      ...data,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);

  if (error) {
    console.log('errror', error.code);
    logger.error('Error updating profile', error, { userId });
    const friendly = getSupabaseDbError(error.code || 'db_update_failed');
    captureFriendlyError(error, friendly, { userId });
    return { success: false, error: friendly };
  }

  return { success: true };
}

export function getDaysUntilInterview(interviewDate: string | null): number | null {
  if (!interviewDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const interview = new Date(interviewDate + 'T00:00:00');
  const diffTime = interview.getTime() - today.getTime();

  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getUrgencyLevel(daysUntil: number | null): 'urgent' | 'soon' | 'upcoming' | 'comfortable' | null {
  if (daysUntil === null) return null;
  if (daysUntil <= 7) return 'urgent';
  if (daysUntil <= 14) return 'soon';
  if (daysUntil <= 30) return 'upcoming';
  return 'comfortable';
}

export function getUrgencyMessage(urgencyLevel: ReturnType<typeof getUrgencyLevel>): string {
  switch (urgencyLevel) {
    case 'urgent':
      return 'Every practice session counts. Let\'s make them count!';
    case 'soon':
      return 'You have time, but consistent practice is key.';
    case 'upcoming':
      return 'Perfect timing to build your confidence.';
    case 'comfortable':
      return 'You\'re starting early — that\'s a great strategy!';
    default:
      return '';
  }
}
